import { formatCurrency, cn } from "@/lib/utils";
import { parseDecimal } from "@/backend/types";
import { Spinner } from "@/components/spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  AlertCircleIcon,
  ReceiptIcon,
} from "lucide-react";
import type { Deposit, Withdrawal } from "@/backend/types";
import { useI18n } from "@/features/i18n";

type TransferHistoryTabProps = {
  deposits: Deposit[];
  withdrawals: Withdrawal[];
  isLoading: boolean;
};

type TransferItem = {
  id: string;
  type: "deposit" | "withdrawal";
  amount: number;
  status: string;
  timestamp: Date;
  address: string;
  txSignature: string | null;
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "credited":
    case "completed":
    case "approved":
      return CheckCircleIcon;
    case "pending":
    case "processing":
      return ClockIcon;
    case "failed":
    case "rejected":
      return XCircleIcon;
    default:
      return AlertCircleIcon;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "credited":
    case "completed":
    case "approved":
      return "text-bid-foreground bg-bid";
    case "pending":
    case "processing":
      return "text-yellow-500 bg-yellow-500/10";
    case "failed":
    case "rejected":
      return "text-ask-foreground bg-ask";
    default:
      return "text-muted-foreground bg-muted";
  }
};

const truncateAddress = (address: string) => {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export const TransferHistoryTab = ({
  deposits,
  withdrawals,
  isLoading,
}: TransferHistoryTabProps) => {
  const { formatTradeDate } = useI18n();

  // Combine and sort transfers
  const transfers: TransferItem[] = [
    ...deposits.map((d) => ({
      id: d.id,
      type: "deposit" as const,
      amount: parseDecimal(d.amount_usd),
      status: d.status,
      timestamp: new Date(d.created_at),
      address: d.deposit_address,
      txSignature: d.tx_signature,
    })),
    ...withdrawals.map((w) => ({
      id: w.id,
      type: "withdrawal" as const,
      amount: parseDecimal(w.amount_usd),
      status: w.status,
      timestamp: new Date(w.created_at),
      address: w.destination_address,
      txSignature: w.tx_signature,
    })),
  ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  if (isLoading) {
    return (
      <div className="rounded-xl bg-card p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </div>
    );
  }

  if (!transfers.length) {
    return (
      <div className="rounded-xl bg-card p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <ReceiptIcon className="size-12 mb-3 opacity-50" />
          <p className="text-sm font-medium">No transfers in the selected period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-card border border-border/50 shadow-sm dark:shadow-none overflow-hidden">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b border-border/50">
        <h3 className="text-sm font-semibold text-foreground">Transfer History</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {transfers.length} transfer{transfers.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Table Header */}
      <div className="hidden md:grid md:grid-cols-5 gap-4 px-4 md:px-6 py-3 bg-muted/30 text-xs font-medium text-muted-foreground">
        <div>Type</div>
        <div>Amount</div>
        <div>Status</div>
        <div>Address</div>
        <div className="text-right">Date</div>
      </div>

      {/* Transfers List */}
      <ScrollArea className="h-[500px]">
        <div className="divide-y divide-border/30">
          {transfers.map((transfer) => {
            const StatusIcon = getStatusIcon(transfer.status);
            const statusColor = getStatusColor(transfer.status);
            const isDeposit = transfer.type === "deposit";

            return (
              <div
                key={transfer.id}
                className="px-4 md:px-6 py-4 hover:bg-muted/20 transition-colors"
              >
                {/* Mobile Layout */}
                <div className="md:hidden space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "p-2 rounded-lg",
                          isDeposit
                            ? "text-bid-foreground bg-bid"
                            : "text-ask-foreground bg-ask"
                        )}
                      >
                        {isDeposit ? (
                          <ArrowDownIcon className="size-4" />
                        ) : (
                          <ArrowUpIcon className="size-4" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-sm">
                          {isDeposit ? "Deposit" : "Withdrawal"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTradeDate(transfer.timestamp)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={cn(
                          "font-semibold",
                          isDeposit ? "text-bid-foreground" : "text-ask-foreground"
                        )}
                      >
                        {isDeposit ? "+" : "-"}${formatCurrency(transfer.amount, 2)}
                      </div>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded capitalize",
                          statusColor
                        )}
                      >
                        <StatusIcon className="size-3" />
                        {transfer.status}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {truncateAddress(transfer.address)}
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid md:grid-cols-5 gap-4 items-center">
                  {/* Type */}
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "p-1.5 rounded-lg",
                        isDeposit
                          ? "text-bid-foreground bg-bid"
                          : "text-ask-foreground bg-ask"
                      )}
                    >
                      {isDeposit ? (
                        <ArrowDownIcon className="size-3.5" />
                      ) : (
                        <ArrowUpIcon className="size-3.5" />
                      )}
                    </div>
                    <span className="font-medium text-sm">
                      {isDeposit ? "Deposit" : "Withdrawal"}
                    </span>
                  </div>

                  {/* Amount */}
                  <div
                    className={cn(
                      "font-semibold",
                      isDeposit ? "text-bid-foreground" : "text-ask-foreground"
                    )}
                  >
                    {isDeposit ? "+" : "-"}${formatCurrency(transfer.amount, 2)}
                  </div>

                  {/* Status */}
                  <div>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 text-xs px-2 py-1 rounded capitalize",
                        statusColor
                      )}
                    >
                      <StatusIcon className="size-3" />
                      {transfer.status}
                    </span>
                  </div>

                  {/* Address */}
                  <div className="text-sm text-muted-foreground font-mono">
                    {truncateAddress(transfer.address)}
                  </div>

                  {/* Date */}
                  <div className="text-sm text-muted-foreground text-right">
                    {formatTradeDate(transfer.timestamp)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
